import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField,
  MenuItem, RadioGroup, FormControlLabel, Radio, CircularProgress, Avatar, Chip,
  FormHelperText, alpha, useTheme, useMediaQuery,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ReplacementStaffPicker from "./ReplacementStaffPicker";
import GradeSubjectGroups from "./GradeSubjectGroups";
import {
  useSwitchClassStaffMutation, useCheckStaffAvailabilityMutation, useGetScheduleDetailsQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

/**
 * Reasons a staff switch can happen. "Other" forces free text that becomes the
 * REAL stored reason — the bare word "Other" is rejected by the backend.
 */
const SWITCH_REASONS = [
  "Staff illness", "Staff unavailable", "Leave", "Scheduling conflict",
  "Workload adjustment", "Administrative change", "Other",
];
const REASON_MAX = 500;

const fmtTime = (t?: string) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const d = new Date(); d.setHours(h, m || 0);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(d);
};
const hhmm = (t?: string) => String(t || "").slice(0, 5);
const initials = (n: string) =>
  (n || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";

/** One label/value cell in the occurrence summary. */
const Ctx: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Typography>
    <Typography sx={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{value ?? "—"}</Typography>
  </Box>
);

/**
 * Switch the teacher on one occurrence, or on every eligible future occurrence
 * in the same series.
 *
 * The candidate list comes from the occurrence's OWN date/time/mode/location via
 * the replacement-staff endpoint, so it contains only teachers who cover every
 * required grade-subject pair AND are genuinely free in that slot — never the
 * full staff list with the busy ones greyed out. The original teacher is never
 * preselected, and the backend re-validates qualification and availability under
 * a row lock at save time, so a stale picker response cannot be committed.
 */
const SwitchStaffDialog: React.FC<{
  open: boolean;
  row: any;
  onClose: () => void;
  onSuccess?: () => void;
}> = ({ open, row, onClose, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showMessage, showErrorMessage } = useNotifier();

  const [switchStaff, { isLoading: switching }] = useSwitchClassStaffMutation();
  const [checkAvailability] = useCheckStaffAvailabilityMutation();

  const occId = row?.id;
  const { data: detailsResp, isFetching: detailsLoading } = useGetScheduleDetailsQuery(occId ?? 0, { skip: !open || !occId });
  const details: any = detailsResp?.data;
  const cls = details?.class;
  const ses = details?.session;
  const occStudents: any[] = useMemo(() => (Array.isArray(details?.students) ? details.students : []), [details]);
  const gradeSubjects: any[] = useMemo(() => (Array.isArray(details?.grade_subjects) ? details.grade_subjects : []), [details]);

  const [newStaffId, setNewStaffId] = useState("");
  const [scope, setScope] = useState<"single" | "future">("single");
  const [reasonSel, setReasonSel] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");
  const [tried, setTried] = useState(false);
  const [affected, setAffected] = useState<{ count: number; conflicts: any[] } | null>(null);
  const [checking, setChecking] = useState(false);

  // Hard reset on open / occurrence change — nothing may leak between sessions.
  useEffect(() => {
    if (!open) return;
    setNewStaffId(""); setScope("single");
    setReasonSel(""); setCustomReason(""); setNotes("");
    setTried(false); setAffected(null); setChecking(false);
  }, [open, occId]);

  const isRecurring = !!row?.isRecurring;
  const studyMode = (cls?.is_online ?? row?.mode === "Online") ? "online" : "in_person";
  const occDate = useMemo(() => {
    const d = ses?.scheduled_date || row?.scheduledDate;
    return d ? String(d).slice(0, 10) : "";
  }, [ses, row]);
  const startTime = hhmm(ses?.start_time || row?.startTime);
  const endTime = hhmm(ses?.end_time || row?.endTime);
  const currentStaffName = details?.staff?.name || row?.staffName || "Unassigned";

  const effectiveReason = reasonSel === "Other" ? customReason.trim() : reasonSel;
  const reasonValid = reasonSel !== "" && (reasonSel !== "Other" || customReason.trim().length > 0);

  // How many occurrences a future-scope switch would touch. Uses the same
  // endpoint the backend re-runs at save time, so the number shown is the number
  // the server will act on — not a client-side guess from the visible rows.
  useEffect(() => {
    if (!open || !newStaffId || scope !== "future" || !occId) { setAffected(null); return; }
    let alive = true;
    setChecking(true);
    (async () => {
      try {
        const res = await checkAvailability({
          ...(occId != null ? { occurrenceId: occId } : { scheduleId: row?.scheduleId }),
          newStaffId: Number(newStaffId), applyToFuture: true,
        }).unwrap();
        if (!alive) return;
        const d: any = res?.data ?? {};
        setAffected(res?.status
          ? { count: Number(d.checkedCount ?? 0), conflicts: Array.isArray(d.conflicts) ? d.conflicts : [] }
          : null);
      } catch { if (alive) setAffected(null); }
      finally { if (alive) setChecking(false); }
    })();
    return () => { alive = false; };
  }, [open, newStaffId, scope, occId, row?.scheduleId, checkAvailability]);

  const blockedByConflicts = scope === "future" && !!affected?.conflicts?.length;
  const canSubmit = !!newStaffId && reasonValid && !switching && !checking && !blockedByConflicts;

  const submit = async () => {
    setTried(true);
    if (!newStaffId || !reasonValid) return;
    try {
      const res = await switchStaff({
        ...(occId != null ? { occurrenceId: occId } : { scheduleId: row?.scheduleId }),
        newStaffId: Number(newStaffId), scope, applyToFuture: scope === "future",
        reason: effectiveReason,
        reason_type: reasonSel === "Other" ? "other" : reasonSel,
        notes,
      }).unwrap();
      if (res.status) { showMessage(res.message || "Staff switched."); onSuccess?.(); onClose(); }
      else {
        // Save-time rejection (became busy / not qualified / concurrent change) —
        // keep the dialog open and drop the stale pick so the user re-chooses.
        showErrorMessage(res.message || "The selected staff member is no longer available. Please choose another teacher.");
        setNewStaffId(""); setAffected(null);
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to switch staff.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : "14px" } }}>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <SwapHorizIcon fontSize="small" /> Switch Staff
      </DialogTitle>

      <DialogContent>
        {detailsLoading && !details ? (
          <Box sx={{ textAlign: "center", py: 5 }}><CircularProgress size={24} /></Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

            {/* ── Occurrence summary ── */}
            <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: alpha("#0f172a", 0.02), border: "1px solid rgba(15,23,42,0.08)" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, gap: 1.25 }}>
                <Ctx label="Date" value={occDate ? new Date(occDate + "T00:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—"} />
                <Ctx label="Time" value={`${fmtTime(startTime)} – ${fmtTime(endTime)}`} />
                <Ctx label="Enrollment Type" value={cls?.program_type ? String(cls.program_type).replace(/^\w/, (c: string) => c.toUpperCase()) : (row?.programType || "—")} />
                <Ctx label="Class Type" value={cls?.class_type || row?.classType || "—"} />
                <Ctx label="Study Mode" value={studyMode === "online" ? "Online" : "In Person"} />
                <Ctx label="Location" value={studyMode === "online" ? "Not applicable" : (cls?.location || row?.locationName || "—")} />
              </Box>

              {/* Grade → Subject, grouped. Never flattened into "Grade 1, Grade 3 Science". */}
              {!!gradeSubjects.length && (
                <Box sx={{ mt: 1.25, pt: 1.25, borderTop: "1px dashed rgba(15,23,42,0.12)" }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.03em", mb: 0.5 }}>
                    Subjects by Grade
                  </Typography>
                  <GradeSubjectGroups groups={gradeSubjects} dense />
                </Box>
              )}

              <Box sx={{ mt: 1.25, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Chip size="small" icon={<GroupsOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                  label={`${occStudents.length} student${occStudents.length === 1 ? "" : "s"} assigned`}
                  sx={{ height: 22, fontSize: 11, bgcolor: alpha("#0f172a", 0.05) }} />
                <Chip size="small" icon={<EventRepeatIcon sx={{ fontSize: "14px !important" }} />}
                  label={isRecurring ? "Recurring series" : "One-off session"}
                  sx={{ height: 22, fontSize: 11, bgcolor: alpha("#0f172a", 0.05) }} />
              </Box>
            </Box>

            {/* ── Current staff ── */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Current Staff</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: "10px", border: "1px solid rgba(15,23,42,0.12)" }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.dark }}>
                  {initials(currentStaffName)}
                </Avatar>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{currentStaffName}</Typography>
              </Box>
            </Box>

            {/* ── Replacement staff ──
                Only teachers covering EVERY required grade-subject pair who are
                free in this exact slot. The slot is fixed, so no "move the time"
                suggestions are offered. */}
            {occId && occDate && startTime && endTime ? (
              <ReplacementStaffPicker
                occurrenceId={occId}
                makeupDate={occDate}
                startTime={startTime}
                endTime={endTime}
                studyMode={studyMode}
                locationId={cls?.location_id ?? row?.locationId ?? null}
                value={newStaffId}
                onChange={setNewStaffId}
                fixedSlot
                label="Replacement Staff *"
              />
            ) : (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                This session is missing a date or time, so replacement staff cannot be matched.
              </Typography>
            )}
            {tried && !newStaffId && (
              <FormHelperText error sx={{ mt: -1 }}>Select a replacement teacher.</FormHelperText>
            )}

            {/* ── Scope ── */}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.25 }}>Apply to</Typography>
              <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as "single" | "future")}>
                <FormControlLabel value="single" control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: 13 }}>This occurrence only</Typography>} />
                <FormControlLabel value="future" control={<Radio size="small" />} disabled={!isRecurring}
                  label={
                    <Typography sx={{ fontSize: 13, color: isRecurring ? "inherit" : "text.disabled" }}>
                      This and future occurrences
                      {!isRecurring && " — one-off session"}
                    </Typography>
                  } />
              </RadioGroup>
              {scope === "future" && newStaffId && (
                <Box sx={{ mt: 0.5 }}>
                  {checking ? (
                    <Typography sx={{ fontSize: 12, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.75 }}>
                      <CircularProgress size={13} /> Checking future occurrences…
                    </Typography>
                  ) : affected ? (
                    affected.conflicts.length ? (
                      <Box sx={{ p: 1.25, borderRadius: "8px", bgcolor: alpha(theme.palette.error.main, 0.06), border: `1px solid ${alpha(theme.palette.error.main, 0.25)}` }}>
                        <Typography sx={{ fontSize: 12, color: "error.main", fontWeight: 700, mb: 0.5 }}>
                          Unavailable for {affected.conflicts.length} occurrence{affected.conflicts.length === 1 ? "" : "s"} — the switch would be rejected.
                        </Typography>
                        {affected.conflicts.slice(0, 5).map((c: any, i: number) => (
                          <Typography key={i} sx={{ fontSize: 11.5, color: "text.secondary" }}>• {c.label}</Typography>
                        ))}
                        {affected.conflicts.length > 5 && (
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>…and {affected.conflicts.length - 5} more</Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: "success.main", fontWeight: 600 }}>
                        This will switch staff for {affected.count} occurrence{affected.count === 1 ? "" : "s"}.
                      </Typography>
                    )
                  ) : null}
                </Box>
              )}
            </Box>

            {/* ── Reason ── */}
            <TextField select label="Reason *" size="small" fullWidth value={reasonSel}
              onChange={(e) => { setReasonSel(e.target.value); if (e.target.value !== "Other") setCustomReason(""); }}
              error={tried && reasonSel === ""}>
              <MenuItem value="">Select a reason…</MenuItem>
              {SWITCH_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            {reasonSel === "Other" && (
              <TextField label="Specify reason *" size="small" fullWidth value={customReason}
                inputProps={{ maxLength: REASON_MAX }}
                placeholder="Why is the teacher being changed?"
                onChange={(e) => setCustomReason(e.target.value)}
                error={tried && customReason.trim().length === 0}
                helperText={tried && customReason.trim().length === 0 ? "Please enter the reason." : `${customReason.length}/${REASON_MAX}`} />
            )}
            <TextField label="Notes (optional)" size="small" fullWidth multiline rows={2}
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}
          startIcon={switching ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}>
          {switching ? "Switching…" : "Switch Staff"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SwitchStaffDialog;
